import allActions from "./config.json";

import { IMS } from "./services/imsService";

// Get API base path from environment variable, default to localhost for development
const API_PATH = process.env.API_PATH || "https://localhost:8080";

// Build full URLs by combining API_PATH with relative paths from config
export const actions = Object.keys(allActions).reduce((obj, key) => {
  if (key.lastIndexOf("/") > -1) {
    obj[key] = `${API_PATH}${allActions[key]}`;
  }
  return obj;
}, {});

/**
 * Generates a presigned URL for accessing Adobe Creative Cloud Library images
 *
 * This function creates a secure, time-limited URL that allows direct access to images
 * stored in Adobe Creative Cloud Libraries without requiring additional authentication
 * for each request. The URL includes the user's access token and is configured for
 * JPEG format with 2048px size.
 *
 * @param {string} libraryId - The unique identifier for the Creative Cloud Library
 *                            (format: urn:aaid:sc:VA6C2:uuid)
 * @param {string} imageId - The unique identifier for the specific image within the library
 *                          (format: uuid)
 * @returns {string} A presigned URL that can be used to directly access the image
 *
 * @example
 * const imageUrl = getPresignedImageUrl(
 *   'urn:aaid:sc:VA6C2:2029142d-71c7-5878-a9f1-5b649d294a8b',
 *   '550e8400-e29b-41d4-a716-446655440000'
 * );
 * // Returns: https://platform-cs-edge-va6c2.adobe.io/rendition/id/...
 *
 * @throws {Error} If no access token is available (user not authenticated)
 *
 * @see {@link getAvatarUrl} - For getting avatar URLs with fallback logic
 * @see {@link getVisualUrl} - For getting visual URLs with fallback logic
 */
export async function getPresignedImageUrl(libraryId, imageId) {
  // Check if user is authenticated first
  if (!IMS.isSignedIn) {
    throw new Error("User not authenticated. Please log in to access images.");
  }

  const accessToken = await IMS.getAccessToken();

  if (!accessToken) {
    throw new Error(
      "No access token available. Please log in to access images.",
    );
  }

  return `https://platform-cs-edge-va6c2.adobe.io/rendition/id/${libraryId};type=image%2Fjpeg;size=2048?resource=%7B%22reltype%22%3A%22http%3A%2F%2Fns.adobe.com%2Fadobecloud%2Frel%2Fcomponent%22%2C%22component_id%22%3A%22${imageId}%22%2C%22revision%22%3A%220%22%7D&api_key=projectx_webapp&user_token=${accessToken}`;
}

/**
 * Generates a presigned URL from a templated Creative Cloud Library link
 *
 * This function takes a templated link from Adobe Creative Cloud Libraries and converts
 * it into a presigned URL that can be used to directly access images. It extracts the
 * base URL and library ID from the templated link format and constructs a proper
 * presigned URL with authentication.
 *
 * @param {string} templatedLink - The templated link from Creative Cloud Library
 *                                Format: "https://platform-cs-edge-{region}.adobe.io/rendition/id/{libraryId}{;page,size,type}{?resource,component_id,fragment}"
 * @param {string} imageId - The unique identifier for the specific image within the library
 * @returns {string} A presigned URL that can be used to directly access the image
 *
 * @example
 * const imageUrl = getPresignedImageUrlFromTemplatedLink(
 *   'https://platform-cs-edge-irl1.adobe.io/rendition/id/urn:aaid:sc:EU:3e7dae06-e5c3-43a2-a17c-6e6538b666df{;page,size,type}{?resource,component_id,fragment}',
 *   '550e8400-e29b-41d4-a716-446655440000'
 * );
 * // Returns: https://platform-cs-edge-irl1.adobe.io/rendition/id/...
 *
 * @throws {Error} If no access token is available (user not authenticated)
 * @throws {Error} If the templated link format is invalid
 *
 * @see {@link getPresignedImageUrl} - For generating presigned URLs from library and image IDs
 */
export async function getPresignedImageUrlFromTemplatedLink(
  templatedLink,
  imageId,
  size = 2048,
  type = "jpeg",
) {
  // Check if user is authenticated first
  if (!IMS.isSignedIn) {
    throw new Error("User not authenticated. Please log in to access images.");
  }

  const accessToken = await IMS.getAccessToken();

  if (!accessToken) {
    throw new Error(
      "No access token available. Please log in to access images.",
    );
  }

  if (!templatedLink || typeof templatedLink !== "string") {
    throw new Error("Invalid templated link provided.");
  }

  // Extract the base URL and library ID from the templated link
  // Format: "https://platform-cs-edge-{region}.adobe.io/rendition/id/{libraryId}{;page,size,type}{?resource,component_id,fragment}"
  const match = templatedLink.match(
    /^(https:\/\/[^\/]+)\/rendition\/id\/([^{]+)/,
  );

  if (!match) {
    throw new Error(
      "Invalid templated link format. Expected format: https://platform-cs-edge-{region}.adobe.io/rendition/id/{libraryId}...",
    );
  }

  const baseUrl = match[1];
  const libraryId = match[2];

  // Construct the presigned URL using the extracted base URL and library ID
  return `${baseUrl}/rendition/id/${libraryId};type=image%2F${type};size=${size}?resource=%7B%22reltype%22%3A%22http%3A%2F%2Fns.adobe.com%2Fadobecloud%2Frel%2Fcomponent%22%2C%22component_id%22%3A%22${imageId}%22%2C%22revision%22%3A%220%22%7D&api_key=projectx_webapp&user_token=${accessToken}`;
}

/**
 * Gets the avatar URL for a persona with intelligent fallback logic
 *
 * This function provides a robust way to retrieve avatar URLs for personas by checking
 * multiple sources in order of preference. It first attempts to generate a presigned URL
 * from Creative Cloud Libraries, falls back to a direct URL if available, and finally
 * provides a default avatar if no other options are available.
 *
 * @param {object} persona - The persona object containing avatar information
 * @param {string} [persona.avatarId] - The avatar ID from the Creative Cloud Library
 * @param {string} [persona.libraryId] - The Creative Cloud Library ID
 * @param {string} [persona.avatarUrl] - The direct avatar URL (fallback option)
 * @returns {string} The resolved avatar URL, or default avatar if none available
 *
 * @example
 * // With library image
 * const avatar1 = getAvatarUrl({
 *   avatarId: '550e8400-e29b-41d4-a716-446655440000',
 *   libraryId: 'urn:aaid:sc:VA6C2:2029142d-71c7-5878-a9f1-5b649d294a8b'
 * });
 * // Returns: presigned URL from Creative Cloud Library
 *
 * // With direct URL
 * const avatar2 = getAvatarUrl({
 *   avatarUrl: 'https://example.com/avatar.jpg'
 * });
 * // Returns: 'https://example.com/avatar.jpg'
 *
 * // With no avatar data
 * const avatar3 = getAvatarUrl({});
 * // Returns: default avatar URL
 *
 * @see {@link getPresignedImageUrl} - For generating presigned URLs
 * @see {@link getVisualUrl} - For getting visual URLs with similar logic
 */
export function getAvatarUrl(persona) {
  // If avatarUrl is already present, return it synchronously
  if (persona?.avatarUrl) {
    return persona.avatarUrl;
  }

  // Fall back to CC Libraries lookup (async) if we have library info but no avatarUrl
  if (persona?.avatarId && (persona?.libraryId || persona?.id)) {
    return getPresignedImageUrl(
      persona?.libraryId ?? persona?.id,
      persona?.avatarId,
    );
  }

  // Return fallback image
  return "https://mir-s3-cdn-cf.behance.net/project_modules/disp/690bc6105945313.5f84bfc9de488.png";
}

/**
 * Gets the visual URL for a visual element with intelligent fallback logic
 *
 * This function provides a robust way to retrieve visual URLs by checking multiple
 * sources in order of preference. It first attempts to generate a presigned URL from
 * Creative Cloud Libraries, falls back to a direct URL if available, and returns an
 * empty string if no visual is available.
 *
 * @param {object} visual - The visual object containing image information
 * @param {string} [visual.imageId] - The image ID from the Creative Cloud Library
 * @param {string} [visual.libraryId] - The Creative Cloud Library ID
 * @param {string} [visual.url] - The direct image URL (fallback option)
 * @returns {string} The resolved visual URL, or empty string if none available
 *
 * @example
 * // With library image
 * const visual1 = getVisualUrl({
 *   imageId: '550e8400-e29b-41d4-a716-446655440000',
 *   libraryId: 'urn:aaid:sc:VA6C2:2029142d-71c7-5878-a9f1-5b649d294a8b'
 * });
 * // Returns: presigned URL from Creative Cloud Library
 *
 * // With direct URL
 * const visual2 = getVisualUrl({
 *   url: 'https://example.com/image.jpg'
 * });
 * // Returns: 'https://example.com/image.jpg'
 *
 * // With no visual data
 * const visual3 = getVisualUrl({});
 * // Returns: ''
 *
 * @see {@link getPresignedImageUrl} - For generating presigned URLs
 * @see {@link getAvatarUrl} - For getting avatar URLs with similar logic
 */
export function getVisualUrl(visual) {
  // If URL is already present (backend provides presigned URL), return it synchronously
  if (visual?.url) {
    return visual.url;
  }

  // Fall back to CC Libraries lookup (async) if we have library info but no URL
  if (visual?.id && visual?.libraryId) {
    return getPresignedImageUrl(visual?.libraryId, visual?.id);
  }

  // Return fallback image
  return "https://react-spectrum.adobe.com/s2/preview.c3b340d3.png";
}

export async function findPrimaryLogo(brand) {
  const logoToUse = brand?.["rendition_grid"]?.main
    ? brand?.["rendition_grid"]?.main
    : brand?.["thumbnail"];

  if (logoToUse?.type === "application/vnd.adobe.element.colortheme+dcx") {
    return { type: "swatches", swatches: logoToUse?.rendition?.swatches };
  } else if (logoToUse?.type === "href") {
    return {
      type: "link",
      url: await getPresignedImageUrlFromTemplatedLink(
        logoToUse?.templatedLink,
        logoToUse?.componentId,
        640,
        "png",
      ),
    };
  } else {
    return { type: "", url: "" };
  }
}

export async function importAllPersonas() {
  const { personas } = await import("./data/personas.js");

  return personas || [];
}

export const getExtensionFromFilePath = (filePath, defaultType) => {
  return filePath.split("/").pop().split("?")[0].split(".")?.[1] ?? defaultType;
};

export const getQueryLoadingState = (query, { loadMore = true } = {}) => {
  if (!Array.isArray(query)) {
    query = [query];
  }

  const activeQueries = query.filter((q) => q.isEnabled);

  const isSomePending = activeQueries.some((q) => q.isPending);
  const isSomeSuccess = activeQueries.some((q) => q.isSuccess);
  if (activeQueries.some((q) => q.isError)) {
    return "error";
  } else if (isSomeSuccess && isSomePending) {
    return loadMore ? "loadingMore" : "loading";
  } else if (isSomePending) {
    return "loading";
  } else {
    return "idle";
  }
};

const relativeTimeFormat = new Intl.RelativeTimeFormat("en", {
  style: "short",
  // numeric: "auto",
});

export const getRelativeTime = (date, relativeTo = new Date()) => {
  const now = new Date(relativeTo);
  const diffInSeconds = Math.floor((now - new Date(date)) / 1000);

  if (!isFinite(diffInSeconds)) {
    return "";
  }

  if (diffInSeconds < 60) {
    return relativeTimeFormat.format(-diffInSeconds, "second");
  } else if (diffInSeconds < 3600) {
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    return relativeTimeFormat.format(-diffInMinutes, "minute");
  } else if (diffInSeconds < 86400) {
    const diffInHours = Math.floor(diffInSeconds / 3600);
    return relativeTimeFormat.format(-diffInHours, "hour");
  } else if (diffInSeconds < 30 * 86400) {
    const diffInDays = Math.floor(diffInSeconds / 86400);
    return relativeTimeFormat.format(-diffInDays, "day");
  } else if (diffInSeconds < 365 * 86400) {
    const diffInMonths = Math.floor(diffInSeconds / (30 * 86400));
    return relativeTimeFormat.format(-diffInMonths, "month");
  } else {
    const diffInYears = Math.floor(diffInSeconds / (365 * 86400));
    return relativeTimeFormat.format(-diffInYears, "year");
  }
};

export const shouldAddMapUrl = (map, url) => {
  if (!map || !url) {
    return false;
  }
  const stripProtocol = (_url) => _url.replace(/^https?:\/\//, "");

  return !Array.from(map.keys()).some(
    (key) => stripProtocol(key) === stripProtocol(url),
  );
};
